
import { Component, Vue } from 'vue-property-decorator';
import { TopicModel, ITopic } from '@/models/topic';
import TopicCard from '@/components/insights/TopicCard.vue';
import QuestionCard from '@/components/insights/QuestionCard.vue';
import { RecentQuestionsList } from '@/collections/recent_questions';
import { RecentQuestionModel } from '@/models/recent_question';
import { PinnedQuestionsList } from '@/collections/pinned_questions';
import { PinnedQuestionModel } from '@/models/pinned_question';
import { EventBus } from '@/main';
import CopyLinkButton from '@/components/CopyLinkButton.vue';

@Component({
  components: {
    TopicCard,
    QuestionCard,
    CopyLinkButton,
  },
})
export default class InsightsHub extends Vue {
  // Data properties
  public topics: ITopic[] = [];
  public recentQuestions: RecentQuestionModel[] = [];
  public pinnedQuestions: PinnedQuestionModel[] = [];

  // Loading and error states
  public isLoading: boolean = false;
  public error: string = '';
  public isLoadingRecentQuestions: boolean = false;
  public recentQuestionsError: string = '';
  public isLoadingPinnedQuestions: boolean = false;
  public pinnedQuestionsError: string = '';
  
  // UI states
  public showViewAllModal: boolean = false;
  public showPinnedViewAllModal: boolean = false;

  // Collections
  private recentQuestionsCollection: RecentQuestionsList = new RecentQuestionsList();
  private pinnedQuestionsCollection: PinnedQuestionsList = new PinnedQuestionsList();

  // Methods

  // Lifecycle hooks
  public async created(): Promise<void> {
    // Load initial data for Phase 1, Phase 4, and Phase 5
    await Promise.all([
      this.loadTopics(),
      this.loadRecentQuestions(),
      this.loadPinnedQuestions()
    ]);
  }

  public async loadTopics(): Promise<void> {
    this.isLoading = true;
    this.error = '';

    try {
      // Load topics using TopicModel
      this.topics = await TopicModel.all();
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'An unexpected error occurred';
      this.topics = [];
    } finally {
      this.isLoading = false;
    }
  }

  // Phase 4: Recent Questions Implementation
  public async loadRecentQuestions(): Promise<void> {
    this.isLoadingRecentQuestions = true;
    this.recentQuestionsError = '';

    try {
      // Load recent questions (limit 10, sort by last executed)
      await this.recentQuestionsCollection.fetch({
        limit: 10,
        sort_by: 'last_executed_at',
        sort_order: 'desc',
        is_pinned: false, // Only non-pinned questions for recent section
      });

      this.recentQuestions = this.recentQuestionsCollection.items;
      
      // Debug: Log the loaded questions to see measure_code and dimension_code
      // console.log('🔍 [DEBUG] Recent questions loaded:', {
      //   count: this.recentQuestions.length,
      //   questions: this.recentQuestions.map((q) => ({
      //     id: q.id,
      //     question_text: q.question_text,
      //     measure_code: q.measure_code,
      //     dimension_code: q.dimension_code,
      //     instruction: q.instruction,
      //   })),
      // });
    } catch (error) {
      this.recentQuestionsError = error instanceof Error ? error.message : 'Failed to load recent questions';
      this.recentQuestions = [];
    } finally {
      this.isLoadingRecentQuestions = false;
    }
  }

  // Phase 5: Pinned Questions Implementation
  public async loadPinnedQuestions(): Promise<void> {
    this.isLoadingPinnedQuestions = true;
    this.pinnedQuestionsError = '';

    try {
      // Load pinned questions (limit 10, ordered by display_order)
      await this.pinnedQuestionsCollection.fetch({
        limit: 10,
        include_analytics: true,
      });

      this.pinnedQuestions = this.pinnedQuestionsCollection.items;
    } catch (error) {
      this.pinnedQuestionsError = error instanceof Error ? error.message : 'Failed to load pinned questions';
      this.pinnedQuestions = [];
    } finally {
      this.isLoadingPinnedQuestions = false;
    }
  }

  // Question event handlers
  public handleQuestionProcessed(_event: any): void {
    // Handle question processing success
  }

  public handleQuestionFailed(_event: any): void {
    // Handle question processing failure
  }

  // Unpin question event handler
  public async handleQuestionUnpinned(event: { questionId?: number; originalQuestion?: PinnedQuestionModel }): Promise<void> {
    const { questionId, originalQuestion } = event;
    
    try {
      // Show loading state
      this.isLoadingPinnedQuestions = true;
      
      let success = false;
      
      // Try to unpin using the original question model first
      if (originalQuestion && typeof originalQuestion.delete === 'function') {
        success = await originalQuestion.delete();
      } else if (questionId) {
        // Fallback: find the question in our collection and delete it
        const pinnedQuestion = this.pinnedQuestions.find(q => parseInt(q.id.toString(), 10) === questionId);
        if (pinnedQuestion && typeof pinnedQuestion.delete === 'function') {
          success = await pinnedQuestion.delete();
        }
      }
      
      if (success) {
        // Refresh pinned questions list
        await this.loadPinnedQuestions();
        
        // Show success message with updated quota info
        const remaining = 10 - this.pinnedQuestions.length;
        const quotaMessage = remaining === 10 ? 
          'You can now pin up to 10 questions.' : 
          `You can pin ${remaining} more questions.`;
        this.showSuccessMessage(`Question unpinned successfully! ${quotaMessage}`);
      } else {
        this.showErrorMessage('Failed to unpin question');
      }
    } catch (error) {
      // Check if the error indicates that the API call was actually successful
      // (Some delete operations may throw errors even when they succeed)
      console.error('Unpin error:', error);
      
      // Refresh the list anyway to check if the operation succeeded
      await this.loadPinnedQuestions();
      
      // Show success message with updated quota info
      const remaining = 10 - this.pinnedQuestions.length;
      const quotaMessage = remaining === 10 ? 
        'You can now pin up to 10 questions.' : 
        `You can pin ${remaining} more questions.`;
      this.showSuccessMessage(`Question unpinned successfully! ${quotaMessage}`);
    } finally {
      this.isLoadingPinnedQuestions = false;
    }
  }

  // Get report context for questions - matches InsightsDialog/TopicDetail pattern
  public getReportContext(recentQuestion?: RecentQuestionModel): any {
    // Use question's specific measure/dimension if available, otherwise provide defaults
    const measure = recentQuestion?.measure_code ? 
      { code: recentQuestion.measure_code, name: recentQuestion.measure_code } :
      { code: '', name: 'General' };
      
    const dimension = recentQuestion?.dimension_code ?
      { code: recentQuestion.dimension_code, name: recentQuestion.dimension_code } :
      { code: '', name: 'General' };

    return {
      measure,
      dimension,
      filterTimerange: this.getDefaultTimerange(),
      reportColumns: [],
      analyticsType: 'standard',
      filterColumns: [],
      reportParams: {},
      productSegment: null,
      pivotOption: null,
      sortBy: '',
      sortDesc: false,
      calculatedMetrics: [],
      comparisonDate: null,
      filterDimensions: [],
    };
  }

  public getDefaultTimerange(): string[] {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 3); // Last 3 months

    return [
      startDate.toISOString().split('T')[0],
      endDate.toISOString().split('T')[0],
      '3m',
    ];
  }

  // Convert RecentQuestionModel to QuestionCard format
  public convertQuestionForCard(recentQuestion: RecentQuestionModel): any {
    const convertedQuestion = {
      id: recentQuestion.id.toString(),
      question: recentQuestion.question_text,
      instruction: recentQuestion.instruction,
      priority: 'medium' as const,
      category: {
        name: 'Recent Question',
        code: recentQuestion.measure_code,
      },
      icon: recentQuestion.getCategoryIcon(),
    };

    // Debug: Log the question conversion
    // console.log('🔍 [DEBUG] convertQuestionForCard:', {
    //   originalId: recentQuestion.id,
    //   originalMeasureCode: recentQuestion.measure_code,
    //   originalDimensionCode: recentQuestion.dimension_code,
    //   convertedId: convertedQuestion.id,
    //   convertedCategoryCode: convertedQuestion.category.code,
    //   measureCodePreserved: recentQuestion.measure_code === convertedQuestion.category.code,
    // });

    return convertedQuestion;
  }

  // UI interaction methods
  public openViewAllModal(): void {
    this.showViewAllModal = true;
  }

  public closeViewAllModal(): void {
    this.showViewAllModal = false;
  }

  // Pinned Questions UI interaction methods
  public openPinnedViewAllModal(): void {
    this.showPinnedViewAllModal = true;
  }

  public closePinnedViewAllModal(): void {
    this.showPinnedViewAllModal = false;
  }

  // Get report context for pinned questions - matches pattern with recent questions
  public getPinnedQuestionReportContext(pinnedQuestion?: PinnedQuestionModel): any {
    // Use pinned question's specific measure/dimension if available, otherwise provide defaults
    const measure = pinnedQuestion?.measure_code ? 
      { code: pinnedQuestion.measure_code, name: pinnedQuestion.measure_code } :
      { code: '', name: 'General' };
      
    const dimension = pinnedQuestion?.dimension_code ?
      { code: pinnedQuestion.dimension_code, name: pinnedQuestion.dimension_code } :
      { code: '', name: 'General' };

    return {
      measure,
      dimension,
      filterTimerange: this.getDefaultTimerange(),
      reportColumns: [],
      analyticsType: 'standard',
      filterColumns: [],
      reportParams: {},
      productSegment: null,
      pivotOption: null,
      sortBy: '',
      sortDesc: false,
      calculatedMetrics: [],
      comparisonDate: null,
      filterDimensions: [],
    };
  }

  // Convert PinnedQuestionModel to QuestionCard format
  public convertPinnedQuestionForCard(pinnedQuestion: PinnedQuestionModel): any {
    return {
      id: pinnedQuestion.id.toString(),
      question: pinnedQuestion.question_text,
      instruction: pinnedQuestion.instruction,
      priority: pinnedQuestion.priority,
      category: {
        name: 'Pinned Question',
        code: pinnedQuestion.measure_code,
      },
      icon: pinnedQuestion.getCategoryIcon(),
    };
  }

  // Navigation methods



  // Topic navigation - Navigate to TopicDetail page
  public navigateToTopic(topic: ITopic): void {
    // Navigate to TopicDetail page with topic code
    this.$router.push({
      name: 'insights-hub-topic-detail',
      params: { topicCode: topic.code },
    });
  }

  // User feedback helper methods
  public showSuccessMessage(message: string): void {
    EventBus.$emit('show-snackbar', {
      message,
      color: 'success',
    });
  }

  public showErrorMessage(message: string): void {
    EventBus.$emit('show-snackbar', {
      message,
      color: 'error',
    });
  }

  // Quota status computed properties
  public get quotaStatus(): { used: number, total: number, remaining: number, isFull: boolean } {
    const used = this.pinnedQuestions.length;
    const total = 10;
    const remaining = total - used;
    const isFull = used >= total;
    
    return { used, total, remaining, isFull };
  }

  public get quotaStatusColor(): string {
    const { remaining } = this.quotaStatus;
    if (remaining === 0) return 'error';
    if (remaining <= 2) return 'warning';
    return 'success';
  }

  // Placeholder methods for future phases
  // These will be implemented in later phases


  // Component cleanup
  public beforeDestroy(): void {
    // Any cleanup needed for Phase 1
  }
}
